import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { PhotoCapture, type CapturedPhoto } from '@/components/synapse/PhotoCapture';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { formatRelativeTime } from '@/lib/relativeTime';
import { PatientDetailsFields } from './PatientDetailsFields';
import {
  emptyPatientDetails,
  hasPatientDetailsErrors,
  resolveDateOfBirth,
  validatePatientDetails,
  type PatientDetailsDraft,
  type PatientDetailsErrors,
} from './patientDetails';
import {
  useApproveSelfRegistration,
  useRejectSelfRegistration,
  useSelfRegistration,
} from './useSelfRegistrations';
import { usePatientPhotoUpload } from './usePatients';

interface Props {
  submissionId: string;
  onApproved: (patientId: string) => void;
  onDone: () => void;
}

/**
 * Staff review of one self-registration: check what the patient entered, fix
 * anything wrong, add the photo, then register them — or dismiss it.
 *
 * The same field components the patient filled in are reused here, so the
 * reviewer is looking at the submission in exactly the form it was given. The
 * photo lives on this side of the flow (not the public form) because the
 * authenticated presigned-upload path already exists and works; an anonymous
 * S3 write would be a far larger surface for a field reception can capture in
 * two seconds at the counter.
 */
export function ReviewRegistrationPane({ submissionId, onApproved, onDone }: Props) {
  const { t } = useTranslation();
  const { data: submission, isLoading, error } = useSelfRegistration(submissionId);
  const approve = useApproveSelfRegistration();
  const reject = useRejectSelfRegistration();
  const uploadPhoto = usePatientPhotoUpload();

  // The reviewer's edits, if any. Null means "nothing typed yet, show what the
  // patient submitted". Tagged with the submission they belong to so they can
  // never be shown under a different one — the call site also passes a `key`,
  // but correctness must not depend on a caller remembering to.
  const [edits, setEdits] = useState<{
    submissionId: string;
    value: PatientDetailsDraft;
  } | null>(null);
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PatientDetailsErrors>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmingReject, setConfirmingReject] = useState(false);
  // Set when the patient was created but the photo upload failed. Holds the new
  // patient's id so the reviewer can still get to the record they just made.
  const [registeredWithoutPhoto, setRegisteredWithoutPhoto] = useState<string | null>(
    null,
  );

  // Derived DURING RENDER, not seeded by an effect. An effect would leave one
  // commit where the submission has loaded but the draft is still null — which
  // the "could not load" guard below reads as a failure, flashing a red error on
  // a request that succeeded. Deriving means the form is correct on first paint.
  const details: PatientDetailsDraft | null = submission
    ? ((edits?.submissionId === submission.id ? edits.value : null) ??
      emptyPatientDetails({
        fullName: submission.fullName,
        sex: submission.sex,
        dob: submission.dateOfBirth,
        phone: submission.phone ?? '',
        nationalId: submission.nationalId ?? '',
      }))
    : null;

  useEffect(() => {
    return () => {
      if (photo) URL.revokeObjectURL(photo.previewUrl);
    };
  }, [photo]);

  const busy = approve.isPending || reject.isPending || uploadPhoto.isPending;

  // Registered, but the photo didn't make it. Checked FIRST: approving
  // invalidates the queue, so the submission query is refetching right now and
  // a loading guard above this would swallow the one message the reviewer needs.
  // The submission is spent either way — the form must not be offered again.
  if (registeredWithoutPhoto) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-[13.5px] text-warn" role="alert">
          {t('selfRegistrations.approvedButPhotoFailed')}
        </p>
        <Button onClick={() => onApproved(registeredWithoutPhoto)}>
          {t('selfRegistrations.openPatient')}
          <Icon name="arrow_up_right" size={16} />
        </Button>
      </div>
    );
  }

  if (isLoading || (!submission && !error)) {
    return <p className="py-10 text-center text-[13px] text-muted">{t('common.loading')}</p>;
  }

  // `details` is derived from `submission`, so it is non-null exactly when the
  // submission is — no separate failure mode to guard.
  if (error || !submission || !details) {
    return (
      <p className="py-10 text-center text-[13px] text-destructive">
        {t('selfRegistrations.couldNotLoad')}
      </p>
    );
  }

  // Already handled by someone else while this tab sat open.
  if (submission.status !== 'pending') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-[13.5px] text-muted">
          {t('errors.CONFLICT')}
        </p>
        <Button variant="secondary" onClick={onDone}>
          {t('selfRegistrations.backToQueue')}
        </Button>
      </div>
    );
  }

  async function handleApprove() {
    if (!details || !submission) return;
    setActionError(null);

    const fullName = details.fullName.trim();
    // The patient typed these on a public form; the reviewer is the one who has
    // to correct a NIC that will not pass, so surface it on their screen rather
    // than as a failed approval.
    const errors: PatientDetailsErrors = {
      ...validatePatientDetails(details),
      fullName: fullName ? null : t('patients.quickCreate.fullNameRequired'),
    };
    setFieldErrors(errors);
    if (hasPatientDetailsErrors(errors)) return;

    const dateOfBirth = resolveDateOfBirth(details.dob, details.age);
    const phone = details.phone.trim();
    const nationalId = details.nationalId.trim();

    let patientId: string;
    try {
      // Send the resolved values explicitly rather than relying on the server's
      // "omitted means keep": the reviewer may have CLEARED a field the patient
      // filled in, and only an explicit null expresses that.
      const result = await approve.mutateAsync({
        id: submission.id,
        input: {
          fullName,
          sex: details.sex,
          dateOfBirth,
          phone: phone || null,
          nationalId: nationalId || null,
        },
      });
      patientId = result.patientId;
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? translateApiError(err, { fallback: t('selfRegistrations.approveFailed') })
          : t('selfRegistrations.approveFailed'),
      );
      return;
    }

    // The patient EXISTS now. A photo failure must not look like an approval
    // failure, or the reviewer will retry and hunt for a second patient that
    // was never created.
    if (photo) {
      try {
        await uploadPhoto.mutateAsync({
          patientId,
          blob: photo.blob,
          contentType: photo.contentType,
        });
      } catch (err) {
        // Log with context: the on-screen message is deliberately generic, so
        // this is the only thing that distinguishes "storage is down for every
        // upload" from "this one blob was rejected" when a clinic reports it.
        console.error('[ReviewRegistration] photo upload failed after approval', {
          patientId,
          submissionId,
          err,
        });
        // Stop here rather than navigating on. Calling onApproved would unmount
        // this component before the message paints — the reviewer would land on
        // a patient with no photo and no idea why.
        setRegisteredWithoutPhoto(patientId);
        return;
      }
    }

    onApproved(patientId);
  }

  async function handleReject() {
    if (!submission) return;
    setActionError(null);
    try {
      await reject.mutateAsync(submission.id);
      setConfirmingReject(false);
      onDone();
    } catch (err) {
      setConfirmingReject(false);
      setActionError(
        err instanceof ApiError
          ? translateApiError(err, { fallback: t('selfRegistrations.rejectFailed') })
          : t('selfRegistrations.rejectFailed'),
      );
    }
  }

  return (
    <div className="flex w-full max-w-[560px] flex-col gap-[18px]">
      <div>
        <h2 className="text-xl font-semibold tracking-[-0.02em]">
          {t('selfRegistrations.reviewTitle')}
        </h2>
        <p className="mt-1 text-[13px] text-muted">
          {t('selfRegistrations.submittedAgo', {
            when: formatRelativeTime(submission.submittedAt, t),
          })}
        </p>
      </div>

      {submission.nationalIdUnreadable && (
        <div
          className="flex items-center gap-2.5 rounded-md border border-line bg-paper-2 px-3.5 py-3"
          role="alert"
        >
          <Icon name="alert" size={16} className="shrink-0 text-warn" />
          <span className="text-[12.5px] leading-snug text-muted">
            {t('selfRegistrations.nationalIdUnreadable')}
          </span>
        </div>
      )}

      <Field label={t('patients.quickCreate.photo')} optional>
        {() => (
          <PhotoCapture
            name={details.fullName}
            previewUrl={photo?.previewUrl ?? null}
            onCapture={setPhoto}
            onRemove={() => setPhoto(null)}
            busy={busy}
          />
        )}
      </Field>

      <PatientDetailsFields
        value={details}
        onChange={(value) => setEdits({ submissionId: submission.id, value })}
        errors={fieldErrors}
        disabled={busy}
      />

      {actionError && (
        <p className="text-[12.5px] text-destructive" role="alert">
          {actionError}
        </p>
      )}

      <div className="flex items-center gap-2.5 border-t border-line pt-5">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setConfirmingReject(true)}
          disabled={busy}
        >
          {reject.isPending ? t('selfRegistrations.rejecting') : t('selfRegistrations.reject')}
        </Button>
        <div className="flex-1" />
        <Button type="button" onClick={handleApprove} disabled={busy}>
          {approve.isPending ? t('selfRegistrations.approving') : t('selfRegistrations.approve')}
          {!approve.isPending && <Icon name="arrow_up_right" size={16} />}
        </Button>
      </div>

      {confirmingReject && (
        <Modal
          title={t('selfRegistrations.rejectConfirm')}
          description={t('selfRegistrations.rejectConfirmBody')}
          onClose={() => setConfirmingReject(false)}
          isPending={reject.isPending}
          footer={
            <>
              <Button
                variant="secondary"
                full
                onClick={() => setConfirmingReject(false)}
                disabled={reject.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="danger" full onClick={handleReject} disabled={reject.isPending}>
                {reject.isPending
                  ? t('selfRegistrations.rejecting')
                  : t('selfRegistrations.reject')}
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-muted">{submission.fullName}</p>
        </Modal>
      )}
    </div>
  );
}
